import _ from "lodash";
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchRecipes, saveRecipe } from "../services/recipeService";

const RecipeContext = createContext();

const RecipeProvider = ({ children }) => {
  const { t } = useTranslation();
  const [recipes, setRecipes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const includesSearchQuery = (field, query) => (field?.toLowerCase() || "").includes(query.toLowerCase());
  const getArrayString = (array) => (array || []).map((item) => item.toLowerCase()).join(" ");

  const filterRecipes = useCallback((recipes, query) => {
    return recipes.filter((recipe) => {
      return includesSearchQuery(recipe.title, query) || includesSearchQuery(recipe.description, query) || includesSearchQuery(getArrayString(recipe.tags), query);
    });
  }, []);

  // Persist debounced function across renders
  const debouncedFetchRef = useRef(
    _.debounce(async (query) => {
      setLoading(true);
      try {
        const allRecipes = await fetchRecipes();
        if (!Array.isArray(allRecipes)) throw new Error("Something went wrong requesting recipes - received tea instead");

        const filteredRecipes = filterRecipes(allRecipes || [], query);
        setRecipes(filteredRecipes);
        setError(null);
      } catch (err) {
        console.error(t("errors.loadingRecipes"), err);
        setError(t("errors.loadingRecipes"));
      } finally {
        setLoading(false);
      }
    }, 300),
  );

  useEffect(() => {
    debouncedFetchRef.current(searchQuery);
    return () => debouncedFetchRef.current.cancel();
  }, [searchQuery, filterRecipes, t]);

  const addNewRecipe = useCallback(
    async (recipe) => {
      try {
        setLoading(true);
        const savedRecipe = await saveRecipe(recipe);
        setRecipes((prevRecipes) => [...prevRecipes, savedRecipe]);
        setError(null);
        return savedRecipe;
      } catch (err) {
        console.error(t("errors.addingRecipe"), err);
        setError(t("errors.addingRecipe"));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  const copyRecipe = useCallback(
    async (existingRecipe, userId) => {
      if (!existingRecipe || !userId) return;
      try {
        setLoading(true);
        const copiedRecipe = {
          ...existingRecipe,
          id: undefined, // Ensures a new ID is assigned
          title: `Copy ${existingRecipe.title}`,
          createdBy: userId,
          isPublic: false,
          createdAt: new Date().toISOString(),
          copiedFrom: existingRecipe.id,
        };
        const savedRecipe = await saveRecipe(copiedRecipe);
        setRecipes((prevRecipes) => [...prevRecipes, savedRecipe]);
        setError(null);
        return savedRecipe;
      } catch (err) {
        console.error(t("errors.copyingRecipe"), err);
        setError(t("errors.copyingRecipe"));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  const updateExistingRecipe = useCallback(
    async (updatedRecipe) => {
      try {
        setLoading(true);
        // Validate that the recipe exists
        const existingRecipe = recipes.find((r) => r.id === updatedRecipe.id);
        if (!existingRecipe) {
          throw new Error(t("recipeNotFound"));
        }
        // Update via the service
        const savedRecipe = await saveRecipe(updatedRecipe);
        // Update local state with the new recipe info
        setRecipes((prevRecipes) => prevRecipes.map((recipe) => (recipe.id === savedRecipe.id ? savedRecipe : recipe)));
        setError(null);
        return savedRecipe;
      } catch (err) {
        console.error(t("errors.updatingRecipe"), err);
        setError(t("errors.updatingRecipe"));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [recipes, t],
  );

  return (
    <RecipeContext.Provider
      value={{
        recipes,
        searchQuery,
        loading,
        error,
        addNewRecipe,
        copyRecipe,
        updateExistingRecipe,
        setSearchQuery,
      }}
    >
      {children}
    </RecipeContext.Provider>
  );
};

export { RecipeContext, RecipeProvider };

